import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { AiOrb } from "../../../shared/ui/ai-orb";
import { SOFT, TURN_ORB_SIZE } from "./soft-tokens";

/** How many lines of work show at once; older ones scroll off the top. */
export const MAX_VISIBLE_STEPS = 4;
const GREEN = "#1A7A44";

/**
 * The wait, as work (chat kit C3).
 *
 * The orb, turning faster, stays where the answer's avatar will be, so the
 * bead never jumps when the text lands. Beside it:
 *
 *   · before the server has said anything, three typing dots;
 *   · once it narrates (one line per tool the agent runs, already in the
 *     guest's language), a short checklist: finished lines get a green tick,
 *     the line it is on now gets a breathing gold dot. At most four show.
 *
 * "writing" is the second half of the wait: the places are in and the prose is
 * not, so every server line is done and the live line is the writing itself.
 */
export function ThinkingIndicator({
  steps,
  stage = "searching",
}: {
  steps?: string[];
  stage?: "searching" | "writing";
}) {
  const { t } = useTranslation();
  // The agent can repeat a step (three detail reads in a row); one line says it.
  const lines = (steps ?? [])
    .map((s) => s.trim())
    .filter((s, i, all) => Boolean(s) && s !== all[i - 1]?.trim());
  const writing = t("ai_concierge.writing", { defaultValue: "Writing your answer…" });
  const searching = t("ai_concierge.thinking_v2", { defaultValue: "Looking through trusted picks…" });
  const all = stage === "writing" ? [...lines, writing] : lines;
  const visible = all.slice(-MAX_VISIBLE_STEPS);
  const current = visible.length - 1;
  const label = all.at(-1) ?? searching;

  return (
    <div
      className="flex items-start gap-2.5 py-1"
      role="progressbar"
      aria-label={label}
      aria-live="polite"
    >
      <AiOrb size={TURN_ORB_SIZE} thinking />
      {visible.length === 0 ? (
        <span
          className="mt-0.5 inline-flex items-center gap-1 rounded-full px-3 py-2.5"
          style={{ background: "rgba(17,14,57,0.055)" }}
          data-testid="typing-dots"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              aria-hidden="true"
              className="mj-stay-dot h-1.5 w-1.5 rounded-full"
              style={{ background: SOFT.ink, animationDelay: `${i * 120}ms` }}
            />
          ))}
          <span className="sr-only">{searching}</span>
        </span>
      ) : (
        <ol
          className="flex min-w-0 flex-col gap-1.5 rounded-[16px] px-3 py-2.5"
          style={{ background: "rgba(17,14,57,0.055)" }}
          data-testid="thinking-steps"
        >
          {visible.map((line, i) => {
            const done = i < current;
            return (
              <li
                // Index from the end keeps a line's key stable as older ones
                // scroll off, so it does not replay its entrance.
                key={`${all.length - visible.length + i}:${line}`}
                className="mj-fade-in flex min-w-0 items-start gap-2 text-[12.5px] leading-[18px]"
                style={{ color: SOFT.ink }}
                data-state={done ? "done" : "current"}
              >
                <span className="mt-px grid h-4 w-4 shrink-0 place-items-center" aria-hidden="true">
                  {done ? (
                    <Check size={13} color={GREEN} strokeWidth={3} />
                  ) : (
                    <span
                      className="h-2 w-2 animate-pulse rounded-full motion-reduce:animate-none"
                      style={{ background: SOFT.gold }}
                    />
                  )}
                </span>
                <span className="line-clamp-2 min-w-0 break-words">{line}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
